import type {ReactNode} from 'react';
import React from 'react';
import {CUSTOM_UNLOCK_CONTROL_MARGIN} from './custom-item-unlock-control.types';
import {CUSTOM_UNLOCK_CONTROL_ID} from './custom-item-unlock-control.types';
import {ActiveSelection} from '@postermywall/fabricjs-2';
import type {CustomItemControlPositions} from '@Components/poster-editor/components/custom-item-controls/components/single-item-custom-controls/single-item-custom-controls.types';
import {degreesToRadians} from '@Utils/math.util';
import {CUSTOM_ITEM_CONTROL_WITH_ICON_SIDE, CUSTOM_ITEM_DIMENSIONS_CONTROL_LONGER_SIDE} from '@Components/poster-editor/components/custom-item-controls/custom-item-controls.types';
import {toggleLockStateForSelectedItem, unlockAllLockedItemsInActiveSelection} from '@Components/poster-editor/library/poster-editor-library';
import {BaseCustomItemControl} from '@Components/poster-editor/components/custom-item-controls/components/base-custom-item-control/base-custom-item-control';
import {ControlType} from '@Components/poster-editor/components/custom-item-controls/components/base-custom-item-control/base-custom-item-control.types';
import type {CustomItemControlsProps} from '@Components/poster-editor/components/custom-item-controls/components/custom-item-controls-wrapper/custom-item-controls-wrapper.types';

export function CustomItemUnlockControl(props: CustomItemControlsProps): ReactNode | null {
  const getCustomUnlockControlPosition = (): CustomItemControlPositions => {
    const itemWidth = (props.positionProperties.width * props.positionProperties.scaleX + props.positionProperties.strokeWidth) * props.positionProperties.posterScale;

    const leftInitial = props.positionProperties.left * props.positionProperties.posterScale;
    const topInitial = props.positionProperties.top * props.positionProperties.posterScale;

    const xCorrectionDueToItemWidth = (itemWidth / 2) * Math.cos(degreesToRadians(props.positionProperties.angle));
    const yCorrectionDueToItemWidth = (itemWidth / 2) * Math.sin(degreesToRadians(props.positionProperties.angle));

    const x = CUSTOM_UNLOCK_CONTROL_MARGIN * Math.sin(degreesToRadians(props.positionProperties.angle));
    const y = CUSTOM_UNLOCK_CONTROL_MARGIN * Math.cos(degreesToRadians(props.positionProperties.angle));

    const left = leftInitial + xCorrectionDueToItemWidth - Math.ceil(CUSTOM_ITEM_DIMENSIONS_CONTROL_LONGER_SIDE / 2 - x);
    const top = topInitial + yCorrectionDueToItemWidth - Math.ceil(CUSTOM_ITEM_DIMENSIONS_CONTROL_LONGER_SIDE / 2) - y;

    return {left, top, angle: props.positionProperties.angle};
  };

  const onUnlockSelection = (): void => {
    const page = window.posterEditor?.whiteboard?.pages.getPage(props.pageHashedId);
    if (!page) {
      return;
    }

    const activeSelection = page.activeSelection.getActiveObject();
    if (!activeSelection) {
      return;
    }

    if (activeSelection instanceof ActiveSelection) {
      unlockAllLockedItemsInActiveSelection();
    } else {
      toggleLockStateForSelectedItem();
    }
  };

  const getUnlockControl = (): ReactNode | null => {
    const page = window.posterEditor?.whiteboard?.pages.getPage(props.pageHashedId);
    if (!page) {
      return null;
    }

    const activeSelection = page.activeSelection.getActiveObject();
    if (!activeSelection) {
      return null;
    }

    if (!activeSelection.lockMovementX) {
      return null;
    }

    const positionAndSize = getCustomUnlockControlPosition();

    return (
      <BaseCustomItemControl
        icon={'icon-lock'}
        controlId={CUSTOM_UNLOCK_CONTROL_ID}
        top={positionAndSize.top}
        left={positionAndSize.left}
        width={CUSTOM_ITEM_CONTROL_WITH_ICON_SIDE}
        height={CUSTOM_ITEM_CONTROL_WITH_ICON_SIDE}
        angle={positionAndSize.angle}
        borderRadius={'50%'}
        isVisible
        onPointerDown={onUnlockSelection}
        controlType={ControlType.CLICK}
      />
    );
  };

  return getUnlockControl();
}
